import find from 'lodash/find';
import get from 'lodash/get';
import some from 'lodash/some';

import { RESPONSES } from 'xm-constants/messageProperties';
import { isGetIncidentStep } from 'xm-constants/getIncidentStep';
import { createTemplateParser } from '@xmatters/common/utils/string';
import { messageStaticProperties } from 'utils/messageStaticProperties';
import { propertyToTriggerTypeMap } from 'reducers/flowDesigner/triggerOutputs';
import {
  isFormBasedTrigger,
  scriptStepTypes,
} from '@xmatters/common/constants/scriptSteps';
import {
  INCIDENT_DETAILS_PREFIX,
  isNotifyToEngageTrigger,
  notifyToEngageProperties,
} from 'xm-constants/incidentTriggers';

const VarType = {
  event: 'event',
  constant: 'constant',
  output: 'output',
  triggerOutput: 'triggerOutput',
  staticEventRegex: 'staticEventRegex',
  recipient: 'recipient',
  sender: 'sender',
  alert: 'alert',
  other: 'other',
};

const TRIGGERS_TO_EXCLUDE_INPUT_PROPS_FOR = [
  'INBOUND_EMAIL',
  'FORM_TRIGGER',
  'CUSTOM_HTTP',
  'AUTOMATION_TRIGGER',
  'EBFD_INCIDENT_RESPONSE_TRIGGER',
];

export const placeholderVarRegex = {
  [VarType.event]: /^event\.properties\.(.*)$/,
  [VarType.constant]: /^constants\.(.*)$/,
  [VarType.output]: /^(\w{8}-\w{4}-\w{4}-\w{4}-\w{12})\.(.+)$/,
  [VarType.triggerOutput]:
    /^triggerOutput(STATUS|DELIVERYSTATUS|RESPONSE|ANNOTATION|ESCALATION|TARGETEDRECIPIENTFAILURE|INBOUND_EMAIL|FORM_TRIGGER)\.(.+)$/,
  [VarType.staticEventRegex]:
    /^event\.(eventId|status|id|created|priority|targetedRecipients|initiator)$/,
  [VarType.recipient]: /^person\.(.*)$/,
  [VarType.sender]: /^sender\.(.*)$/,
  [VarType.alert]: /^alert\.(.*)$/,
  [VarType.other]: /^other\.(.*)$/,
};

export const CUSTOM_PROPERTY_PREFIX = 'properties.';

export function getEventVariable(eventName, blockId) {
  const variable = `event.properties.${eventName}`;
  return blockId ? `${blockId}.${variable}` : variable;
}

export function getConstantVariable(constName) {
  return `constants.${constName}`;
}

export function getOutputVariable(entityId, outputName) {
  return `${entityId}.${outputName}`;
}

function getTriggerVariableValue(
  triggerPropName,
  id,
  { triggeredBy, integrationType },
) {
  return isFormBasedTrigger(integrationType)
    ? `${id}.${triggerPropName}`
    : `triggerOutput${triggeredBy || integrationType}.${triggerPropName}`;
}

export function getTriggerVariable(triggerPropName, flowTrigger) {
  // email trigger does not have step in flowTrigger.
  if (flowTrigger.step) {
    return getTriggerVariableValue(
      triggerPropName,
      flowTrigger.id,
      flowTrigger.step,
    );
  }
  return getTriggerVariableValue(triggerPropName, flowTrigger.id, flowTrigger);
}

export const isTriggerFormBased = (trigger, includeGrouped) =>
  trigger &&
  (includeGrouped || !trigger.groupingBlockId) &&
  !TRIGGERS_TO_EXCLUDE_INPUT_PROPS_FOR.includes(get(trigger, 'step.type'));

export const areTriggersFormBased = triggers =>
  triggers.length > 0 && triggers.every(isTriggerFormBased);

/**
 * Given input value, i.e. something like `text ${variable} and \${not a variable}`,
 * split it into text snippets ([`text `, ` and \${not a variable}`]) and  variables ([`variable`])
 * @param {string} inputValue
 * @returns {string[]} result.textSnippets
 * @returns {string[]} result.variables
 */
export const parsePlaceholderInputValue = createTemplateParser(
  '${',
  '}',
  '\\',
  true,
);

function getMessagePropName(messagePropList, propId, intl) {
  const prop = find(messagePropList, { id: propId });
  return prop ? intl.formatMessage({ id: prop.nameId }) : undefined;
}

const formatTriggerType = (intl, triggerType) =>
  intl.formatMessage({ id: `VARIABLE_KEY_${triggerType.toUpperCase()}` });

const matchVarPartsByVarType = {
  [VarType.event]: ({ match, intl }) => ({
    type: intl.formatMessage({ id: 'EVENT_PROPERTIES' }),
    value: match[1],
  }),
  [VarType.staticEventRegex]: ({ match, intl }) => ({
    type: intl.formatMessage({ id: 'EVENT' }),
    value: match[1],
  }),
  [VarType.constant]: ({ match, intl }) => ({
    type: intl.formatMessage({ id: 'CONSTANTS' }),
    value: match[1],
  }),
  [VarType.recipient]: ({ match, intl }) => ({
    type: intl.formatMessage({ id: 'RECIPIENT' }),
    value:
      getMessagePropName(messageStaticProperties.recipient, match[0], intl) ||
      match[1],
  }),
  [VarType.sender]: ({ match, intl }) => ({
    type: intl.formatMessage({ id: 'SENDER' }),
    value:
      getMessagePropName(messageStaticProperties.sender, match[0], intl) ||
      match[1],
  }),
  [VarType.alert]: ({ match, intl }) => ({
    type: intl.formatMessage({ id: 'ALERT' }),
    value:
      getMessagePropName(
        messageStaticProperties.alertDetails,
        match[0],
        intl,
      ) || match[1],
  }),
  [VarType.other]: ({ match, intl }) => {
    const fullToken = match[0];
    if (fullToken === RESPONSES) {
      return {
        type: intl.formatMessage({ id: 'VARIABLE_KEY_RESPONSE' }),
        value: intl.formatMessage({ id: 'VARIABLE_KEY_RESPONSE' }),
      };
    }
    return {
      // Currently only company properties (company name, company xmatters URL) are under `other`.
      // If that changes in the future it may be practical to make the regex more precise,
      // e.g. `/^other\.(company.+)$/` or similar
      type: intl.formatMessage({ id: 'COMPANY' }),
      value:
        getMessagePropName(messageStaticProperties.company, fullToken, intl) ||
        match[1],
    };
  },
  [VarType.triggerOutput]: ({ match, intl }) => ({
    type: formatTriggerType(intl, match[1]),
    value: match[2],
  }),
  [VarType.output]: ({
    match,
    blocksById,
    intl,
    forms,
    incidentTypePropertiesById,
  }) => {
    const [, blockOrFormId, outputName] = match;
    const form = forms.find(formItem => formItem.id === blockOrFormId);

    if (form) {
      const triggerType = propertyToTriggerTypeMap[outputName];
      if (triggerType) {
        return {
          type: `${form.name} - ${formatTriggerType(intl, triggerType)}`,
          value: outputName,
        };
      }

      return { type: form.name, value: outputName };
    }

    const block = blocksById[blockOrFormId];
    let type = block?.label || 'unknown';

    if (block?.step?.type === scriptStepTypes.CREATE_ALERT) {
      const triggerType = propertyToTriggerTypeMap[outputName];
      if (triggerType) {
        type += ` - ${formatTriggerType(intl, triggerType)}`;
      }
    }

    /**
     * Ideally we should likely continue trying to convince PMs and UX that we can remove this `if` block.
     * The gist is that trigger outputs are of form `Escalations > escalation.reason`,
     * while all other block outputs are of form `${block.label} > escalation.reason`.
     * In the cases of triggers, ${block.label} looks like `$formName - Escalations`.
     *
     * In short: removing this `if` block would simply prepend the form name and a dash (`$formName - `).
     * Which, if there are Create Alert steps, would make it clearer which trigger is being referenced.
     */
    const triggerType = block?.step?.triggeredBy;
    const groupingBlockId = block?.groupingBlockId;
    if (triggerType && !groupingBlockId) {
      type = formatTriggerType(intl, triggerType);
    } else if (groupingBlockId) {
      // Try to find a valid response:
      const parent = blocksById[groupingBlockId];
      if (parent?.type === 'RESPONSES') {
        type = formatTriggerType(intl, 'RESPONSE');
      }
    }

    if (
      isGetIncidentStep(block?.step) &&
      some(incidentTypePropertiesById, { name: outputName })
    ) {
      const incidentProperty = {
        type: `${type} > ${intl.formatMessage({ id: 'INCIDENT_PROPERTIES' })}`,
        value: outputName,
      };

      const property = incidentTypePropertiesById[incidentProperty.value];
      if (property) {
        incidentProperty.value = property.name;
      }

      return incidentProperty;
    }

    if (outputName.includes(INCIDENT_DETAILS_PREFIX)) {
      const incidentDetailsResult = {
        type: `${type} > ${intl.formatMessage({ id: 'INCIDENT_DETAILS' })}`,
        value: outputName.replace(INCIDENT_DETAILS_PREFIX, ''),
      };

      const property = incidentTypePropertiesById[incidentDetailsResult.value];
      if (property) {
        incidentDetailsResult.value = property.name;
      }

      return incidentDetailsResult;
    }

    const hasNotifyToEngage = find(block?.ports, port =>
      isNotifyToEngageTrigger(port?.triggerType),
    );

    if (
      hasNotifyToEngage &&
      notifyToEngageProperties.includes(outputName.toLowerCase())
    ) {
      return {
        type: `${type} > ${intl.formatMessage({ id: 'NOTIFY_TO_ENGAGE' })}`,
        value: outputName,
      };
    }

    return { type, value: outputName };
  },
};

export function getVariableParts({
  variable,
  blocksById,
  intl,
  forms,
  incidentTypePropertiesById,
}) {
  if (!variable) {
    return { type: 'unknown', value: variable };
  }

  let match;
  const varPartsGetter = find(matchVarPartsByVarType, (getter, type) => {
    match = variable.match(placeholderVarRegex[type]);
    return match;
  });
  return varPartsGetter
    ? varPartsGetter({
        match,
        intl,
        variable,
        blocksById: blocksById || {},
        forms: forms || [],
        incidentTypePropertiesById: incidentTypePropertiesById || {},
      })
    : { type: 'unknown', value: variable };
}
